import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, IconButton, styled, Typography } from '@mui/material';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import { httpClient } from '../../../../api/http/http-client';
import { AsanConfirmation, toAsanSession } from 'app/features/sign/asan';
import { FilterButton } from '../../../../components/FilterButton';
import ArrowLeftIcon from '../../../../components/Icons/ArrowLeftIcon';
import InfoIcon from '../../../../components/Icons/InfoIcon';
import { getTheme } from '../../../../components/ThemeProvider/theme';
import { colors, themePalette } from '../../../../components/ThemeProvider/themeColors';
import { useCustomNavigate } from '../../../../utils/customHooks/useCustomNavigate';
import AddEmployeeModal from './add-employee';
import ExcelExample from './components/ExcelExample';
import StepCircle from './components/StepCircle';
import EmployeesTable from './employees-table';
import { AddEmployeeSchema, createAddEmployeeSchema } from './model/schema';
import { EmployeeFormFields, EmployeeRow, SignSalaryCardRequest } from './model/types';
import { parseExcel } from './model/utils';
import SuccessOperation from './success-operation';
import UploadArea from './upload-area';
import WrongFile from './wrong-file';

const defaultEmployeeForm: EmployeeFormFields = {
  fullName: '',
  pin: '',
  serialType: { label: 'AA', value: 'AA' },
  serialNumber: '',
  phone: '',
  position: '',
};

const getEmployeeValidation = (employees: EmployeeRow[]) => {
  const duplicateIndices = new Set<number>();
  const finCount = new Map<string, number>();
  const serialCount = new Map<string, number>();
  const gsmCount = new Map<string, number>();

  const hasIncompleteEmployee = employees.some(
    e => !e.customerName || !e.fin || !e.serial || !e.gsm || !e.employeePosition
  );

  employees.forEach(e => {
    if (e.fin) finCount.set(e.fin, (finCount.get(e.fin) || 0) + 1);
    if (e.serial) serialCount.set(e.serial, (serialCount.get(e.serial) || 0) + 1);
    if (e.gsm) gsmCount.set(e.gsm, (gsmCount.get(e.gsm) || 0) + 1);
  });

  employees.forEach((e, i) => {
    if (
      (e.fin && finCount.get(e.fin)! > 1) ||
      (e.serial && serialCount.get(e.serial)! > 1) ||
      (e.gsm && gsmCount.get(e.gsm)! > 1)
    ) {
      duplicateIndices.add(i);
    }
  });

  return {
    hasIncompleteEmployee,
    duplicateIndices,
    hasDuplicates: duplicateIndices.size > 0,
  };
};

const OrderPage = () => {
  const { t } = useTranslation('salary-card');
  const navigate = useCustomNavigate();
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type');
  const [asanConfirmation, setAsanConfirmation] = useState(false);
  const [wrongFileOpen, setWrongFileOpen] = useState(false);
  const [showEmployeeValidationErrors, setShowEmployeeValidationErrors] = useState(false);
  const [showSuccessOperation, setShowSuccessOperation] = useState(false);
  const [isClicked, setIsClicked] = useState(false);

  const [employeeModal, setEmployeeModal] = useState<{
    open?: boolean;
    idx?: number | null;
  }>({
    open: false,
    idx: null,
  });

  const [employees, setEmployees] = useState<EmployeeRow[]>([]);
  const business_key = searchParams.get('business_key');
  const validation = getEmployeeValidation(employees);

  const methods = useForm<AddEmployeeSchema>({
    resolver: yupResolver(createAddEmployeeSchema(t)),
    defaultValues: defaultEmployeeForm,
  });

  const handleBack = () => {
    navigate('salary-card');
  };

  const handleFilter = (filter: string) => {
    navigate(`salary-card/order?type=${filter}&business_key=${business_key}`);
  };

  const { data: signResponse, mutate: signMutate } = useMutation({
    mutationKey: ['sign-salary-card'],
    retry: 1,
    mutationFn: async (data: SignSalaryCardRequest) => {
      return await httpClient.post<any>(
        `/api-web/v1/main/salary_cards?business_key=${data.business_key}`,
        data
      );
    },
    onSuccess: response => {
      if (response.form_id === 'asan_wait_popup') {
        setAsanConfirmation(true);
      }
    },
  });

  const handleSign = () => {
    if (validation.hasIncompleteEmployee || validation.hasDuplicates) {
      setShowEmployeeValidationErrors(true);

      return;
    }

    setShowEmployeeValidationErrors(false);

    setIsClicked(true);

    signMutate({
      business_key,
      employees,
      action: 'sign',
    });
  };

  const handleEmployeeSubmit = (data: EmployeeFormFields, idx?: number) => {
    const employee = {
      customerName: data.fullName,
      serial: data.serialType.label + data.serialNumber,
      fin: data.pin,
      gsm: `994${data.phone.replace(/\s+/g, '').replace(/\(/g, '').replace(/\)/g, '')}`,
      employeePosition: data.position,
    };

    setEmployees(prev => {
      if (idx >= 0) {
        return prev.map((emp, i) => (i === idx ? employee : emp));
      }

      return [...prev, employee];
    });

    setEmployeeModal({ open: false, idx: null });
  };

  const editEmployee = (idx: number, data: EmployeeRow) => {
    setEmployeeModal({ open: true, idx });
    console.log(employeeModal.idx);
    methods.reset({
      fullName: data.customerName,
      pin: data.fin,
      serialType: { label: data.serial.slice(0, 2), value: data.serial.slice(0, 2) },
      serialNumber: data.serial.slice(2),
      phone: data.gsm.replace(/^994/, ''),
      position: data.employeePosition,
    });

    console.log(methods.getValues());
  };

  const deleteEmployee = (index: number) => {
    setEmployees(prev => prev.filter((_, i) => i !== index));
  };

  const handleFileSelect = (file: File) => {
    parseExcel(file)
      .then(data => {
        if (!data || data.length === 0) {
          setEmployees([]);
          setWrongFileOpen(true);
        } else {
          setEmployees(data);
        }
      })
      .catch(() => {
        setEmployees([]);
        setWrongFileOpen(true);
      });
  };

  const adviceForSingle = () => {
    return (
      <Box
        sx={{
          padding: '12px',
          maxWidth: '420px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          backgroundColor: colors.info[50],
          border: `1px solid ${colors.info[300]}`,
          borderRadius: '8px',
        }}
      >
        <InfoIcon sx={{ width: '24px', height: '24px' }} color='secondary' />
        <Typography fontSize={16} fontWeight={600} lineHeight='24px'>
          {t('order.singleAdvice')}
        </Typography>
      </Box>
    );
  };

  const handleAsanStatus = (data: any) => {
    if (data?.result === 'success') {
      setShowSuccessOperation(true);
    }
  };

  if (showSuccessOperation) {
    return <SuccessOperation />;
  }

  return (
    <Box pb={20}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 4,
          flexWrap: 'wrap',
        }}
      >
        <IconButton onClick={handleBack} sx={{ p: 0 }}>
          <ArrowLeftIcon
            stroke={themePalette.text_and_icon.primary}
            sx={{ width: '24px', height: '24px' }}
          />
        </IconButton>
        <Typography fontSize={20} fontWeight={600} lineHeight='28px'>
          {t('orderTitle')}
        </Typography>
      </Box>
      <Box
        sx={{
          backgroundColor: 'white',
          boxShadow: '0px 0px 32px 0px rgba(0, 0, 0, 0.06)',
          borderRadius: '12px',
        }}
      >
        <Container>
          <Typography fontSize={20} fontWeight={600} lineHeight='28px'>
            {t('employeeInfo')}
          </Typography>
          <Box display='flex' gap={1} sx={{ flexWrap: 'wrap' }}>
            <FilterButton
              isActive={type === 'excel'}
              onClick={() => handleFilter('excel')}
              color={'tertiary'}
            >
              {t('order.filters.excel')}
            </FilterButton>
            <FilterButton
              isActive={type === 'single'}
              onClick={() => handleFilter('single')}
              color={'tertiary'}
            >
              {t('order.filters.single')}
            </FilterButton>
          </Box>
          {type === 'single' && adviceForSingle()}
          {type === 'excel' && (
            <>
              <Box display='flex' alignItems='center' gap={1}>
                <StepCircle number={1} />
                <Typography fontSize={16} fontWeight={600} lineHeight='24px'>
                  {t('order.excel.step1')}
                </Typography>
              </Box>
              <ExcelExample />
              <Box display='flex' alignItems='center' gap={1}>
                <StepCircle number={2} />
                <Typography fontSize={16} fontWeight={600} lineHeight='24px'>
                  {t('order.excel.step2')}
                </Typography>
              </Box>
              <UploadArea
                acceptedExtensions={{ 'application/vnd.ms-excel': ['.xlsx'] }}
                maxSizeMB={30}
                onFileSelect={handleFileSelect}
              />
            </>
          )}
        </Container>
      </Box>
      {(type === 'single' || employees.length > 0) && (
        <EmployeesTable
          employees={employees}
          showValidationErrors={showEmployeeValidationErrors}
          hasIncompleteEmployee={validation.hasIncompleteEmployee}
          duplicateIndices={validation.duplicateIndices}
          onAdd={() => setEmployeeModal({ open: true })}
          onEdit={editEmployee}
          onDelete={deleteEmployee}
          onDeleteAll={() => setEmployees([])}
        />
      )}
      <FixedBottomAction>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 2,
            width: '100%',
            maxWidth: '720px',
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              fontSize={14}
              fontWeight={500}
              lineHeight='20px'
              color={themePalette.text_and_icon.secondary}
            >
              {t('employeeCount')}:
            </Typography>
            <Typography fontSize={14} fontWeight={600} lineHeight='20px'>
              {employees.length > 0 ? employees.length : t('order.notAdded')}
            </Typography>
          </Box>
          <Button disabled={employees.length === 0 || isClicked} onClick={handleSign} size='large'>
            {t('sign')}
          </Button>
        </Box>
      </FixedBottomAction>
      <FormProvider {...methods}>
        <AddEmployeeModal
          onSubmit={handleEmployeeSubmit}
          idx={employeeModal.idx}
          closeModal={() => setEmployeeModal({ open: false, idx: null })}
          dialogProps={{
            open: employeeModal.open,
            fullWidth: true,
            maxWidth: 'sm',
            TransitionProps: {
              onExited: () => methods.reset(defaultEmployeeForm),
            },
          }}
        />
      </FormProvider>
      <AsanConfirmation
        open={asanConfirmation}
        close={setAsanConfirmation}
        session={toAsanSession(signResponse)}
        handleRedirect={handleAsanStatus}
        csi='direct'
      />
      <WrongFile open={wrongFileOpen} setOpen={setWrongFileOpen} />
    </Box>
  );
};

const theme = getTheme();

const Container = styled('div')({
  borderRadius: '12px',
  padding: 32,
  display: 'flex',
  flexDirection: 'column',
  gap: 24,
  position: 'relative',
  overflow: 'hidden',
  maxWidth: '720px',
  '@media (max-width: 900px)': {
    padding: 16,
  },
});

const FixedBottomAction = styled('div')({
  position: 'fixed',
  bottom: 0,
  left: '341px',
  right: 0,
  width: 'calc(100% - 341px)',
  backgroundColor: colors.base[0],
  borderTop: `1px solid ${colors.base[100]}`,
  padding: '16px 32px',
  display: 'flex',
  justifyContent: 'flex-end',
  zIndex: 1,
  boxSizing: 'border-box',

  [theme.breakpoints.down('md')]: {
    left: 0,
    width: '100%',
    padding: '16px',
  },
});

export default OrderPage;
